import React, { useState, useCallback, useEffect } from "react";
import { CartCount, useToast } from "@swordsweeper/ssi-ui-component-library";
import classnames from "classnames";
import debounce from "lodash/debounce";
import { Link, useNavigate } from "react-router-dom";
import * as CartApi from "@src/api/CartApi";
import * as SavedDistributorProductApi from "@src/api/SavedDistributorProductApi";
import PriceDisplay from "@src/components/common/PriceDisplay/PriceDisplay";
import { useCartStore } from "@src/state/cartState";
import { useSavedForLaterStore } from "@src/state/savedForLaterState";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import styles from "./CartProduct.module.scss"; // Only for productImage style
import ProductDescription from "../ProductDescription";

interface CartProductProps {
  className?: string;
  itemName: string;
  description?: string;
  quantity?: number;
  price?: number;
  pricePerUnit?: number;
  id: string;
  distributorProductId?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  productId?: string;
}

export default function CartProduct({
  className,
  itemName,
  description,
  quantity = 1,
  price,
  pricePerUnit,
  id: cartDistributorProductId,
  distributorProductId,
  imageUrl,
  thumbnailUrl,
  productId,
}: CartProductProps) {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { cart, refreshCart, setCart } = useCartStore();
  const { refreshSavedForLater } = useSavedForLaterStore();
  const [count, setCount] = useState(quantity);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const optimisticallyRemoveCartItem = useCallback(() => {
    if (!cart) return;
    let removedQuantity = 0;
    let removedSubtotal = 0;
    const nextGroupings = cart.distributorProductGroupings.map((grouping) => {
      let groupRemovedQuantity = 0;
      let groupRemovedSubtotal = 0;

      const filterItems = (items?: any[]) => {
        if (!items?.length) return items;
        return items.filter((item) => {
          if (`${item.id}` !== `${cartDistributorProductId}`) {
            return true;
          }
          const qty = item.quantity ?? 1;
          const price = item.livePricing?.price ?? 0;
          groupRemovedQuantity += qty;
          groupRemovedSubtotal += price * qty;
          return false;
        });
      };

      const nextProducts = {
        ...grouping.products,
        available: filterItems(grouping.products?.available),
        backordered: filterItems(grouping.products?.backordered),
        priceUnavailable: filterItems(grouping.products?.priceUnavailable),
        stockUnavailable: filterItems(grouping.products?.stockUnavailable),
      };

      const nextGrouping = {
        ...grouping,
        products: nextProducts,
      };

      if (groupRemovedQuantity > 0) {
        nextGrouping.itemCount = Math.max(
          0,
          (grouping.itemCount ?? 0) - groupRemovedQuantity,
        );
        nextGrouping.subtotal = Math.max(
          0,
          (grouping.subtotal ?? 0) - groupRemovedSubtotal,
        );
        removedQuantity += groupRemovedQuantity;
        removedSubtotal += groupRemovedSubtotal;
      }

      return nextGrouping;
    });

    if (removedQuantity === 0) return;

    setCart({
      ...cart,
      distributorProductGroupings: nextGroupings,
      itemCount: Math.max(0, (cart.itemCount ?? 0) - removedQuantity),
      subtotal: Math.max(0, (cart.subtotal ?? 0) - removedSubtotal),
    });
  }, [cart, cartDistributorProductId, setCart]);

  const debouncedUpdateCartProductQuantity = useCallback(
    debounce(async (newCount: number) => {
      if (newCount === 0) {
        handleRemoveProductFromCart();
      } else {
        setIsLoading(true);
        try {
          await CartApi.updateCartProductQuantity(
            cartDistributorProductId,
            newCount,
          );
          refreshCart();
        } catch (e) {
          console.error(e);
          showToast({
            message: "Error updating quantity",
            variant: "error",
          });
          setCount(quantity);
        } finally {
          setIsLoading(false);
        }
      }
    }, 500),
    [cartDistributorProductId, refreshCart, showToast],
  );

  const handleUpdateCount = async (newCount: number) => {
    if (!isLoading) {
      setCount(newCount);
      debouncedUpdateCartProductQuantity(newCount);
    }
  };

  // Cancel pending debounced updates on component unmount
  useEffect(() => {
    return () => {
      debouncedUpdateCartProductQuantity.cancel();
    };
  }, [debouncedUpdateCartProductQuantity]);

  const handleRemoveProductFromCart = async () => {
    // Cancel any pending debounced quantity updates to prevent race condition
    debouncedUpdateCartProductQuantity.cancel();

    optimisticallyRemoveCartItem();

    if (!isLoading) setIsLoading(true);

    try {
      await CartApi.removeCartProduct(cartDistributorProductId);
      refreshCart();
      showToast({
        message: `${itemName} was removed from your cart`,
        variant: "success",
      });
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error removing product from cart",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProductClick = () => {
    if (productId) {
      navigate(`/product/${productId}/`);
    }
  };

  const handleSaveProductForLater = async () => {
    if (!distributorProductId) {
      showToast({
        message: "Unable to save this product right now.",
        variant: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      await SavedDistributorProductApi.createSavedDistributorProduct(
        distributorProductId,
      );
      await refreshSavedForLater();
      showToast({
        message: "Saved for later",
        variant: "success",
      });

      optimisticallyRemoveCartItem();
      await handleRemoveProductFromCart(); // also remove from cart
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error saving product for later",
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const row = (
    <div
      className={classnames(
        "flex-row space-between padding-y-md position-relative",
        className,
      )}
    >
      {isLoading && <SpinningLoader whiteBackground />}
      <img
        className={classnames(styles.productImage, { clickable: !!productId })}
        src={thumbnailUrl || imageUrl}
        alt={itemName}
        onClick={handleProductClick}
      />

      <div className="flex-1 margin-left-md flex-column space-between">
        <div className="flex-row space-between">
          <div>
            <div
              className={classnames("font-weight-bold text-lg", {
                clickable: !!productId,
              })}
              onClick={handleProductClick}
            >
              {itemName}
            </div>
            {!!description && (
              <ProductDescription
                className="text-sm"
                description={description}
              />
            )}
          </div>
          <div className="margin-left-xl">
            <div className="font-weight-bold text-lg">
              <PriceDisplay
                amount={typeof price === "number" ? price : null}
                unavailableText="Price Unavailable"
              />
            </div>
            <div className="text-xs text-right">
              {typeof pricePerUnit === "number" ? (
                <PriceDisplay amount={pricePerUnit} suffix="/unit" />
              ) : (
                ""
              )}
            </div>
          </div>
        </div>

        <div className="flex-row space-between margin-top-md">
          <div className="flex-row spaced-content-lg">
            <CartCount
              onChange={handleUpdateCount}
              onDelete={handleRemoveProductFromCart}
              value={count}
              maxValue={100000}
              decrementButtonId={`cart-page-decrement-button-${cartDistributorProductId}`}
              valueInputId={`cart-page-value-input-${cartDistributorProductId}`}
              valueDisplayId={`cart-page-value-display-${cartDistributorProductId}`}
              incrementButtonId={`cart-page-increment-button-${cartDistributorProductId}`}
            />

            <span className="text-xs no-user-select flex-row flex-center spaced-content-sm">
              <span
                id={`cart-page-remove-product-button-${cartDistributorProductId}`}
                data-testid={`cart-page-remove-product-button-${cartDistributorProductId}`}
                className="link"
                onClick={handleRemoveProductFromCart}
              >
                Remove
              </span>
              &nbsp;
              <span className="text-lightest">|</span>
              &nbsp;
              <Link
                id={`cart-page-buy-from-different-distributor-link-${cartDistributorProductId}`}
                data-testid={`cart-page-buy-from-different-distributor-link-${cartDistributorProductId}`}
                className="text-default"
                to={`/product/${productId}/`}
              >
                Buy from a different distributor
              </Link>
              &nbsp;
              <span className="text-lightest">|</span>
              &nbsp;
              <span
                id={`cart-page-save-for-later-button-${cartDistributorProductId}`}
                data-testid={`cart-page-save-for-later-button-${cartDistributorProductId}`}
                className="link"
                onClick={handleSaveProductForLater}
              >
                Save for later
              </span>
            </span>
          </div>

          {/* Note: Commented out for Beta due to missing functionality */}
          {/* <div>
            <Badge
              ariaLabel="Greatest savings badge with money bill icon"
              icon={<i className="fa-light fa-money-bill-wave" />}
              label="Greatest Savings"
              variant="success"
            />
          </div> */}
        </div>
      </div>
    </div>
  );

  return row;
}
